/**
 * Serve-mode filters — the grade / homeroom facets plus the sort, as one
 * pure state object.
 *
 * Serve draws its rows from THREE sources that must all answer the same
 * filter: parent student cards (bands 1-2), roster kids with no order today
 * (band 3), and the school-forecast quantity rows. Each carries its grade
 * and its homeroom on different fields, so the axis resolvers below are the
 * single place that knows which field is which — the page, the filter bar,
 * and the tests all work off the resolved axes.
 *
 * Why the campus policy matters here: `groupLabel` is the server-resolved
 * serve bucket, and it is the label the cards and tally rows actually
 * RENDER. Its dimension is the campus's `serveGroupBy`, and its value is the
 * raw Enrollment fact — a GRADE-grouped campus shows "8" where the display
 * field `gradeRangeName` reads "Grade 8". Routing `groupLabel` onto its own
 * axis (rather than guessing) is what keeps a facet option matching the text
 * on the card it filters.
 *
 * Only grade and homeroom are offered: they are the two dimensions the serve
 * payload actually carries per student. TEACHER / ROOM / LUNCH_PERIOD do not
 * reach the client today — extending this module to them means shipping them
 * on the session payload first, not adding an option that filters nothing.
 * (The former per-campus `filterDimensions` knob was removed 2026-09-15; it
 * was never read.)
 */

import { serveFilterAxis, studentGradeAxis, studentHomeroomAxis } from '@ordo/constants/serve-line'

import type { ServeGroupDimension } from '@/api/cafeteria'

/** The serve grid's user-selectable sort. */
export type ServeSortBy = 'first' | 'last' | 'grade' | 'meal'

export const SERVE_SORT_OPTIONS: ReadonlyArray<{ value: ServeSortBy; label: string }> = [
  { value: 'first', label: 'First name' },
  { value: 'last', label: 'Last name' },
  { value: 'grade', label: 'Grade' },
  { value: 'meal', label: 'Meal type' },
]

export interface ServeFilterState {
  /** Selected grade-axis labels; empty = no grade constraint. */
  grades: ReadonlySet<string>
  /** Selected homeroom-axis labels; empty = no homeroom constraint. */
  homerooms: ReadonlySet<string>
  sortBy: ServeSortBy
}

export const EMPTY_SERVE_FILTERS: ServeFilterState = {
  grades: new Set<string>(),
  homerooms: new Set<string>(),
  sortBy: 'first',
}

/** Whether any facet is narrowing the list (the sort is not a filter). */
export function isServeFilterActive(state: ServeFilterState): boolean {
  return state.grades.size > 0 || state.homerooms.size > 0
}

/** Whether the filter bar's Clear affordance has anything to undo. */
export function isServeFilterDirty(state: ServeFilterState): boolean {
  return isServeFilterActive(state) || state.sortBy !== EMPTY_SERVE_FILTERS.sortBy
}

// ---------------------------------------------------------------------------
// Dimension → axis
// ---------------------------------------------------------------------------

/**
 * Which axis a campus's `groupLabel` lands on. `NONE` (one undivided serve
 * list) and any dimension we cannot place carry no axis, so their labels stay
 * out of both facets rather than being filed under a wrong heading. HOMEROOM,
 * TEACHER, ROOM and LUNCH_PERIOD are all the non-grade axis; the facet is
 * titled from the dimension (see `homeroomFacetLabel`) so a room-grouped
 * campus does not read "Homeroom". The rule lives in `@ordo/constants/serve-line`
 * because the server's prep counts (PROD-1484) filter by the same facets.
 */
export function axisForDimension(dimension: ServeGroupDimension | null | undefined): 'grade' | 'homeroom' | null {
  return serveFilterAxis(dimension)
}

/** Display titles for the non-grade facet, matching the Customers app's vocabulary. */
const NON_GRADE_DIMENSION_LABELS: Readonly<Record<string, string>> = {
  HOMEROOM: 'Homeroom',
  TEACHER: 'Teacher',
  ROOM: 'Classroom',
  LUNCH_PERIOD: 'Lunch period',
}

/**
 * Title for the non-grade facet. A campus grouped by rooms or lunch periods
 * gets its own word; everything else (including an unconfigured campus, whose
 * second axis is the student's homeroom fact) reads "Homeroom".
 */
export function homeroomFacetLabel(serveGroupBy: ServeGroupDimension | null | undefined): string {
  if (serveGroupBy == null || axisForDimension(serveGroupBy) !== 'homeroom') {
    return 'Homeroom'
  }
  return NON_GRADE_DIMENSION_LABELS[serveGroupBy] ?? 'Homeroom'
}

// ---------------------------------------------------------------------------
// Per-source axis resolution
// ---------------------------------------------------------------------------

/** The student-card fields the axes read (a slice of `StudentMealGroup`). */
export interface StudentGroupAxes {
  gradeRangeName: string | null
  rosterName: string | null
  /** Server-resolved serve bucket; its dimension is the campus `serveGroupBy`. */
  groupLabel?: string | null
}

/** The tally-row fields the axes read (a slice of `ServeDeliverableRowWire`). */
export interface DeliverableRowAxes {
  gradeGroupName: string | null
  rosterName: string | null
  /** The row's own stamped dimension — authoritative over the campus policy. */
  groupDimension?: string | null
  groupLabel?: string | null
}

/** The roster-card fields the axes read (a slice of `NoOrderStudent`). */
export interface NoOrderStudentAxes {
  gradeLabel: string | null
  homeroom: string | null
}

/**
 * Grade-axis value for a parent student card. The rendered bucket wins when
 * the campus groups by grade (so the facet option is the text on the card);
 * otherwise the grade display field carries the axis on its own.
 */
export function gradeAxisForStudentGroup(
  group: StudentGroupAxes,
  serveGroupBy: ServeGroupDimension | null | undefined,
): string | null {
  return studentGradeAxis(group, serveGroupBy)
}

/** Homeroom-axis value for a parent student card — the mirror of the above. */
export function homeroomAxisForStudentGroup(
  group: StudentGroupAxes,
  serveGroupBy: ServeGroupDimension | null | undefined,
): string | null {
  return studentHomeroomAxis(group, serveGroupBy)
}

/**
 * Grade-axis value for a school tally row. The row carries its OWN stamped
 * dimension, which outranks the campus policy (a line stamped before a policy
 * change keeps its original bucket). Un-stamped legacy rows fall back to the
 * wire's own split: `gradeGroupName` is the AGE_GROUP column, and is null for
 * every other dimension.
 */
export function gradeAxisForDeliverableRow(row: DeliverableRowAxes): string | null {
  const axis = axisForDimension(row.groupDimension as ServeGroupDimension | null | undefined)
  if (axis === 'homeroom') {
    return null
  }
  if (axis === 'grade') {
    return row.groupLabel ?? row.gradeGroupName
  }
  return row.gradeGroupName
}

/** Homeroom-axis value for a school tally row — the mirror of the above. */
export function homeroomAxisForDeliverableRow(row: DeliverableRowAxes): string | null {
  const axis = axisForDimension(row.groupDimension as ServeGroupDimension | null | undefined)
  if (axis === 'grade') {
    return null
  }
  if (axis === 'homeroom') {
    return row.groupLabel ?? row.rosterName
  }
  return row.rosterName
}

// ---------------------------------------------------------------------------
// Facet options — the union across every source, numeric-aware sorted
// ---------------------------------------------------------------------------

export interface ServeFilterFacets {
  grades: string[]
  homerooms: string[]
  /** Title for the non-grade facet ("Homeroom", "Classroom", …). */
  homeroomLabel: string
}

/** Numeric-aware sort so "Grade 9" precedes "Grade 10" and "8A" precedes "10A". */
function sortedLabels(values: Iterable<string>): string[] {
  return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/**
 * Facet options built from the UNFILTERED day: every grade and homeroom
 * present across school tally rows, parent student cards, and roster kids
 * with no order. Deriving them from the data (rather than from any campus
 * configuration) is what keeps the bar useful on an unconfigured campus. A facet with no options is
 * not rendered.
 */
export function buildServeFilterFacets(
  deliverableRows: ReadonlyArray<DeliverableRowAxes>,
  studentGroups: ReadonlyArray<StudentGroupAxes>,
  noOrderStudents: ReadonlyArray<NoOrderStudentAxes>,
  serveGroupBy: ServeGroupDimension | null | undefined,
): ServeFilterFacets {
  const grades = new Set<string>()
  const homerooms = new Set<string>()
  const add = (set: Set<string>, value: string | null): void => {
    if (value) {
      set.add(value)
    }
  }
  for (const row of deliverableRows) {
    add(grades, gradeAxisForDeliverableRow(row))
    add(homerooms, homeroomAxisForDeliverableRow(row))
  }
  for (const group of studentGroups) {
    add(grades, gradeAxisForStudentGroup(group, serveGroupBy))
    add(homerooms, homeroomAxisForStudentGroup(group, serveGroupBy))
  }
  for (const student of noOrderStudents) {
    add(grades, student.gradeLabel)
    add(homerooms, student.homeroom)
  }
  return {
    grades: sortedLabels(grades),
    homerooms: sortedLabels(homerooms),
    homeroomLabel: homeroomFacetLabel(serveGroupBy),
  }
}

/**
 * A facet's rendered options: the day's values, plus any value the operator
 * still has selected that this mealtime does not offer.
 *
 * Without the union, switching mealtimes strands a selection: it keeps
 * filtering, but it is absent from the option list, so it cannot be
 * unticked — and `FilterMultiSelect` falls back to the PLACEHOLDER for a
 * single unknown value, so the pill reads "All grades" while showing none.
 * Keeping the value listed makes the constraint visible and reversible.
 */
export function facetOptionsWithSelected(
  options: ReadonlyArray<string>,
  selected: ReadonlySet<string>,
): Array<{ value: string; label: string }> {
  const merged = selected.size === 0 ? Array.from(options) : sortedLabels(new Set([...options, ...selected]))
  return merged.map((value) => ({ value, label: value }))
}

// ---------------------------------------------------------------------------
// Matchers — one per source, all sharing the same axis semantics
// ---------------------------------------------------------------------------

/**
 * A row passes a facet when the facet is empty (no constraint) or its value
 * on that axis is selected. A row with NO value on a constrained axis is
 * excluded — the operator asked for a named group, and "unknown" is not it.
 * Facets compose as AND: Grade 3 + Homeroom 3B is the intersection.
 */
function matchesFacet(value: string | null, selected: ReadonlySet<string>): boolean {
  return selected.size === 0 || (value != null && selected.has(value))
}

export function studentGroupMatchesFilters(
  group: StudentGroupAxes,
  state: ServeFilterState,
  serveGroupBy: ServeGroupDimension | null | undefined,
): boolean {
  return (
    matchesFacet(gradeAxisForStudentGroup(group, serveGroupBy), state.grades) &&
    matchesFacet(homeroomAxisForStudentGroup(group, serveGroupBy), state.homerooms)
  )
}

export function deliverableRowMatchesFilters(row: DeliverableRowAxes, state: ServeFilterState): boolean {
  return (
    matchesFacet(gradeAxisForDeliverableRow(row), state.grades) &&
    matchesFacet(homeroomAxisForDeliverableRow(row), state.homerooms)
  )
}

export function noOrderStudentMatchesFilters(student: NoOrderStudentAxes, state: ServeFilterState): boolean {
  return matchesFacet(student.gradeLabel, state.grades) && matchesFacet(student.homeroom, state.homerooms)
}

// ---------------------------------------------------------------------------
// Student counts — the grade pill's "2 grades · 7 students" (PROD-1484)
// ---------------------------------------------------------------------------

/** "1 student" / "7 students". */
export function studentCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'student' : 'students'}`
}

/**
 * Ordered students per grade-axis value under the HOMEROOM facet alone: what
 * each grade option counts, so the selected grades always sum to the filtered
 * list the pill sits beside.
 */
export function studentCountsByGrade(
  groups: ReadonlyArray<StudentGroupAxes>,
  state: ServeFilterState,
  serveGroupBy: ServeGroupDimension | null | undefined,
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const group of groups) {
    const grade = gradeAxisForStudentGroup(group, serveGroupBy)
    if (grade && matchesFacet(homeroomAxisForStudentGroup(group, serveGroupBy), state.homerooms)) {
      counts.set(grade, (counts.get(grade) ?? 0) + 1)
    }
  }
  return counts
}
